import React, { useState } from 'react';
import { Form } from '@heroui/react';
import { LargeLogo } from 'icons/logo/large';
import Input from 'ui/input';
import PasswordInput from 'ui/passwordinput';
import Button from 'ui/button';
import Link from 'ui/link';
import { validateEmail } from '@/components/Common/validators';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { auth } from '@/store/asyncActions/auth';
import Alert from 'ui/alert';
import AuthenticationWrapper from 'wrapper/ui/authentication';
import { Fade } from 'react-awesome-reveal';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/microsoftAuthConfig';
import { getOktaAuthorizationUrl } from '../config/oktaAuthConfig';
import SSOModal, { SSOProvider, SSOIcons } from '@/components/Common/Modals/SSOModal';
import { SSOIcon } from 'icons/brand/sso';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showSSOModal, setShowSSOModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { instance } = useMsal();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const res = await dispatch(auth({ email, password }));
    if (typeof res?.payload === 'string') setErrorMessage(res?.payload);
    else setErrorMessage('');
  };

  const handleMicrosoftLogin = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Microsoft login error:', error);
      setErrorMessage('Failed to sign in with Microsoft. Please try again.');
    }
  };

  const handleSamsaraLogin = () => {
    try {
      const authUrl = getOktaAuthorizationUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Samsara login error:', error);
      setErrorMessage('Failed to sign in with Samsara. Please try again.');
    }
  };

  const ssoProviders: SSOProvider[] = [
    {
      id: 'samsara',
      name: 'Samsara',
      icon: <SSOIcons.Samsara />,
      onClick: handleSamsaraLogin,
    },
    {
      id: 'microsoft',
      name: 'Enumerate Microsoft',
      icon: <SSOIcons.EnumerateMicrosoft />,
      onClick: handleMicrosoftLogin,
    },
  ];

  return (
    <AuthenticationWrapper>
      {/* Login Form */}

      <div className="flex min-h-screen">
        {/* Right half - form container */}

        <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
          <div className="w-full max-w-md flex flex-col gap-8">
            <div className="flex items-center w-full justify-center">
              <LargeLogo width={200} height={40} />
            </div>
            <div className="rounded-large h-full bg-content1 shadow-small flex flex-col gap-4 px-8 py-6 overflow-hidden">
              {errorMessage?.length > 0 && (
                <Fade duration={1200}>
                  <Alert
                    color="danger"
                    title={errorMessage}
                    role="alert"
                    aria-live="assertive"
                  />
                </Fade>
              )}
              <div className="text-start">
                <p id="sign-in-heading" className="pb-1 heading-hero">
                  Sign In
                </p>
                <p id="sign-in-subheading" className="sub-heading-hero">
                  Enter your email and password to sign in
                </p>
              </div>
              <Form
                aria-labelledby="sign-in-heading"
                aria-describedby="sign-in-subheading"
                validationBehavior="aria"
                onSubmit={handleSubmit}
                className="-space-y-2"
              >
                <div className="flex flex-col gap-4 w-full">
                  <Input
                    name="email"
                    label="Email"
                    isRequired
                    placeholder="Enter your email"
                    validate={(val) =>
                      val.length <= 5
                        ? null
                        : validateEmail(val)
                        ? null
                        : 'Enter a valid email'
                    }
                    value={email}
                    onChange={(value: string) => setEmail(value)}
                    onClear={() => setEmail('')}
                    size="md+"
                  />
                  <PasswordInput
                    label="Password"
                    placeholder="Enter your password"
                    isRequired
                    value={password}
                    onChange={(value: string) => setPassword(value)}
                    size="md+"
                  />
                </div>

                <div className="flex w-full items-center justify-between px-1">
                  <Link
                    href="/forgot_password"
                    size="sm"
                    underline="none"
                    aria-label="Forgot password"
                  >
                    <span className="text-link-blue">Forgot password?</span>
                  </Link>
                </div>
                <Button
                  isDisabled={false}
                  fullWidth
                  variant="solid"
                  color="primary"
                  type="submit"
                  className="!mt-4"
                  aria-label="Log In"
                >
                  Log In
                </Button>
              </Form>

              {/* Divider
              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 border-t border-divider"></div>
                <span className="text-tiny text-default-500">OR</span>
                <div className="flex-1 border-t border-divider"></div>
              </div>

              {/* SSO Sign In Button */}
              {/* <Button
                fullWidth
                variant="bordered"
                color="default"
                onClick={() => setShowSSOModal(true)}
                className="!mt-0"
                aria-label="Sign in with SSO"
                startContent={<SSOIcon size={24} />}
              >
                Sign in with SSO */}
              {/* </Button> */}

              <div className="flex justify-center text-sm gap-1">
                <span className="text-default-500">View</span>
                <Link
                  href="https://enumerate.ai/privacy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  underline="none"
                  aria-label="Privacy policy"
                >
                  <span className="text-link-blue">privacy policy</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SSO Provider Selection Modal */}
      <SSOModal
        isOpen={showSSOModal}
        onClose={() => setShowSSOModal(false)}
        providers={ssoProviders}
      />
    </AuthenticationWrapper>
  );
};

SignIn.accessType = 'anonymous';

export default SignIn;
